// A tiny, React-free registry of "there is unsaved work on screen right now" tokens.
//
// <VersionGuard/> hard-reloads the page (no prompt) the moment a newer release is
// live. That is right for an idle tab but wrong mid-edit: the staff Schedule sheets
// (New Booking / Change / Multi-pool / Block / Detail edit) hold typed input that a
// silent reload would bin — which is exactly what happened on 2026-09-07 when eight
// releases shipped in a day and wiped every open sheet.
//
// The fix is NOT a path hold (freezing upgrades on /schedule would stall force-upgrade
// on the screen staff live on all day). Instead each sheet registers a token WHILE it
// actually holds unsaved input and releases it the instant the work is saved, discarded,
// closed, or the component unmounts. VersionGuard skips the reload while any token is
// held and fires it as soon as the last one clears.
//
// Deliberately dependency-free (no React, no context/provider) so any component can opt
// in with a single effect without threading props, and the gate is trivially unit-
// testable in a plain node environment.

const tokens = new Set<symbol>();
const listeners = new Set<() => void>();

function notifyCleared() {
  // A bad listener must never wedge the registry — swallow its throw.
  for (const fn of [...listeners]) {
    try {
      fn();
    } catch {
      /* ignore */
    }
  }
}

/**
 * Mark that there is unsaved work on screen. Returns a release fn — call it when the
 * work is saved, discarded, or the component unmounts. The release is idempotent, so it
 * is safe to use directly as a useEffect cleanup (which may run more than once).
 */
export function registerUnsavedWork(): () => void {
  const token = Symbol();
  tokens.add(token);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    tokens.delete(token);
    if (tokens.size === 0) notifyCleared();
  };
}

/** True while any unsaved-work token is held. */
export function hasUnsavedWork(): boolean {
  return tokens.size > 0;
}

/**
 * Be told the instant the LAST token is released, so a deferred upgrade can fire right
 * away instead of waiting for the next poll. Returns an unsubscribe fn.
 */
export function subscribeUnsavedWork(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
